import { useEffect, useState } from 'react';
import { saasApi } from '../../lib/api';

function fmtMoney(cents, currency = 'BRL') {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
  }).format(cents / 100);
}

const EMPTY = {
  slug: '',
  name: '',
  description: '',
  priceCents: 0,
  maxUsers: 1,
  maxTemplates: 5,
  features: '',
  period: 'MONTHLY',
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  function load() {
    setError('');
    return saasApi.listPlans().then(setPlans).catch((e) => setError(e.message));
  }

  useEffect(() => {
    load();
  }, []);

  function set(field) {
    return (e) => setForm({ ...form, [field]: e.target.type === 'number' ? Number(e.target.value) : e.target.value });
  }

  function startCreate() {
    setForm({ ...EMPTY, features: '' });
    setEditing(null);
    setCreating(true);
  }

  function startEdit(plan) {
    setForm({
      slug: plan.slug,
      name: plan.name,
      description: plan.description ?? '',
      priceCents: plan.priceCents,
      maxUsers: plan.maxUsers,
      maxTemplates: plan.maxTemplates,
      features: (plan.features ?? []).join('\n'),
      period: plan.period,
    });
    setEditing(plan);
    setCreating(true);
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    const payload = {
      ...form,
      features: form.features
        .split('\n')
        .map((f) => f.trim())
        .filter(Boolean),
    };
    try {
if (editing) {
        const { slug, ...rest } = payload;
        void slug;
        await saasApi.updatePlan(editing.id, rest);
      } else {
        await saasApi.createPlan(payload);
      }
      setCreating(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deactivate(plan) {
    setError('');
    try {
      await saasApi.removePlan(plan.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="flex flex-col gap-base">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Planos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-2">
            Catálogo de planos oferecidos aos tenants.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors"
        >
          + Novo plano
        </button>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}

      {creating && (
        <form onSubmit={submit} className="bg-surface rounded-2xl shadow-sm p-6 space-y-4">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{editing ? 'Editar plano' : 'Novo plano'}</h3>
          <div className="grid gap-4 md:grid-cols-3">
            <Field label="Nome" value={form.name} onChange={set('name')} placeholder="Profissional" />
            <Field label="Slug" value={form.slug} onChange={set('slug')} placeholder="pro" disabled={!!editing} />
            <Field label="Preço (centavos)" type="number" value={form.priceCents} onChange={set('priceCents')} />
            <Field label="Máx. usuários" type="number" value={form.maxUsers} onChange={set('maxUsers')} />
            <Field label="Máx. templates" type="number" value={form.maxTemplates} onChange={set('maxTemplates')} />
            <label className="block">
              <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Período</span>
              <select value={form.period} onChange={set('period')} className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px]">
                <option value="MONTHLY">Mensal</option>
                <option value="YEARLY">Anual</option>
              </select>
            </label>
            <Field label="Descrição" value={form.description} onChange={set('description')} placeholder="Para pequenas equipes" />
            <label className="block md:col-span-2">
              <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Recursos (um por linha)</span>
              <textarea
                value={form.features}
                onChange={set('features')}
                rows={3}
                className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors">
              Salvar
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
              className="rounded-xl px-4 py-2 font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {plans.map((p) => (
          <div key={p.id} className={`bg-surface rounded-2xl shadow-sm p-6 ${!p.active ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between">
              <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{p.name}</h3>
              <Badge active={p.active}>{p.active ? 'Ativo' : 'Inativo'}</Badge>
            </div>
            <p className="mt-1 text-[10px] text-on-surface-variant">{p.description}</p>
            <p className="mt-4 font-h2 text-h2 text-on-surface">
              {fmtMoney(p.priceCents, p.currency)}
              <span className="text-[11px] text-on-surface-variant">/{p.period === 'MONTHLY' ? 'mês' : 'ano'}</span>
            </p>
            <ul className="mt-4 space-y-1 text-[11px] text-on-surface-variant">
              <li>Máx. {p.maxUsers} usuários</li>
              <li>Máx. {p.maxTemplates} templates</li>
              {(p.features ?? []).map((f) => (
                <li key={f}>• {f}</li>
              ))}
            </ul>
            <div className="mt-4 text-[10px] text-on-surface-variant">Tenants no plano: {p.tenantCount}</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => startEdit(p)} className="rounded-lg bg-surface-container-high px-3 py-1.5 text-[11px] font-medium text-on-surface hover:bg-surface-container-highest transition-colors">
                Editar
              </button>
              {p.active && (
                <button onClick={() => deactivate(p)} className="rounded-lg px-3 py-1.5 text-[11px] font-medium text-error hover:bg-error-container/30 transition-colors">
                  Desativar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Badge({ active, children }) {
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-label-sm ${
        active ? 'bg-tertiary-container text-on-tertiary-container' : 'bg-surface-container-highest text-on-surface-variant'
      }`}
    >
      {children}
    </span>
  );
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">{label}</span>
      <input
        {...props}
        className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
      />
    </label>
  );
}


